import styled from "styled-components";
import { ReactComponent as RefreshIcon } from "assets/svg/refresh.svg";
import { Card } from "Components/Card";
import { FlexColumn } from "Components/Flex";
import { useScrollLockContext } from "contexts/ScrollLockContext";
import { useParams } from "react-router-dom";
import { WinnerRow } from "./WinnerRow";
import { useState } from "react";
import { pluralize } from "Utils/pluralize";
import { SweepsLoader } from "./SweepsSearch";
import { useSetfanSweeps } from "./hooks/useSetfanSweeps";
import { ResetSweepsModal } from "./ActionModal";
import { useWinnersConversations } from "./hooks/useWinnersConversations";

export const SweepsWinnersList = () => {
  const [resetModalOpen, setResetModalOpen] = useState(false);

  const { lockScroll } = useScrollLockContext();
  const { surveyId } = useParams() as { surveyId: string };

  const { activeSweeps, cancelCurrentSweeps } = useSetfanSweeps({ surveyId });

  const { conversations, loading: loadingConversations } =
    useWinnersConversations({
      surveyId,
    });

  if (!activeSweeps || loadingConversations) {
    return <SweepsLoader />;
  }

  return (
    <>
      <StyledCard isElevated style={{ position: "relative" }}>
        <InnerCard>
          <HeaderContainer>
            <FlexColumn>
              <HeaderText>Sweepstakes Winners</HeaderText>
              <HeaderSubtext>{`${pluralize(
                activeSweeps.numberOfWinners,
                "winner",
              )}${
                activeSweeps?.limitToUS ? " | Limited to United States" : ""
              }`}</HeaderSubtext>
            </FlexColumn>
            <ResetContainer
              onClick={() => {
                lockScroll();
                setResetModalOpen(true);
              }}
            >
              <ResetIcon />
              <ResetText>Reset</ResetText>
            </ResetContainer>
          </HeaderContainer>
          <WinnersContainer>
            {activeSweeps.winners.map((winner) => {
              return (
                <WinnerRow
                  key={winner}
                  conversation={conversations[winner]}
                  fanId={winner}
                  surveyId={surveyId}
                />
              );
            })}
          </WinnersContainer>
        </InnerCard>
      </StyledCard>
      <ResetSweepsModal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        onConfirm={cancelCurrentSweeps}
      />
    </>
  );
};

const HeaderSubtext = styled.div`
  color: #333;
  text-align: center;
  font-family: Poppins;
  font-size: 12px;
  line-height: 18px;

  ${({ theme }) => theme.media.tablet} {
    font-size: 15px;
    line-height: 22px;
  }
`;

const HeaderContainer = styled.div`
  height: 71px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;

  ${({ theme }) => theme.media.tablet} {
    flex-direction: row;
    justify-content: center;
    margin-bottom: 0;
  }
`;

const HeaderText = styled.div`
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;

  ${({ theme }) => theme.media.tablet} {
    font-size: 26px;
    line-height: 36px;
  }
`;

const InnerCard = styled.div`
  width: 100%;
`;

const ResetContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 9px;

  ${({ theme }) => theme.media.desktop} {
    position: absolute;
    right: 43px;
  }
`;

const ResetIcon = styled(RefreshIcon)`
  color: #1b0076;
  height: 14px;
  width: 14px;

  ${({ theme }) => theme.media.tablet} {
    width: 22px;
    height: 22px;
  }
`;

const ResetText = styled.span`
  color: #1b0076;
  font-family: Poppins;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;

  ${({ theme }) => theme.mediaQueries.desktop} {
    line-height: 24px;
    font-size: 16px;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 24px;

  ${({ theme }) => theme.media.tablet} {
    padding: 24px 48px 15px 48px;
  }
`;

const WinnersContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > div:not(:last-child) {
    border-bottom: 1px solid #dedede;
  }
`;
